import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Wallet } from "lucide-react";

const eur = (n) => `€${Number(n).toFixed(2)}`;
const day = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "-";

function PaymentRow({ p }) {
  return (
    <div className="grid grid-cols-[1fr_auto] md:grid-cols-[1.2fr_80px_1.5fr_110px_90px] gap-2 py-3 border-b border-neutral-900 font-mono text-xs" data-testid={`earning-payment-${p.id}`}>
      <span className="text-neutral-200">{p.name}</span>
      <span className="text-neon md:order-last text-right">{eur(p.amount)}</span>
      <span className="hidden md:block text-neutral-400">{p.people} PERS.</span>
      <span className="hidden md:block text-neutral-500 truncate">{p.note || "-"}</span>
      <span className="hidden md:block text-neutral-500">{day(p.paid_at)}</span>
      <span className="md:hidden col-span-2 text-neutral-500 text-[10px]">{p.people} PERS. · {p.note || "-"} · {day(p.paid_at)}</span>
    </div>
  );
}

function TableBlock({ t }) {
  return (
    <div className="mt-4" data-testid={`earning-table-${t.table_id}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="label-mono text-white">TAVOLO {t.table_name}</span>
        <span className="font-mono text-xs text-neon">{eur(t.total)} · {t.payments.length} {t.payments.length === 1 ? "CAPARRA" : "CAPARRE"}</span>
      </div>
      {t.payments.map(p => <PaymentRow key={p.id} p={p}/>)}
    </div>
  );
}

function EventBlock({ e }) {
  return (
    <div className="card-dark p-6" data-testid={`earning-event-${e.event_id}`}>
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="label-mono text-neon mb-1">{day(e.event_date)}</div>
          <h2 className="font-display text-2xl">{e.event_title}</h2>
        </div>
        <div className="font-display text-2xl text-neon">{eur(e.total)}</div>
      </div>
      {e.tables.map(t => <TableBlock key={t.table_id} t={t}/>)}
    </div>
  );
}

export default function Earnings() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const isAffiliate = user?.role === "affiliate";
  useEffect(() => { if (isAffiliate) { api.get("/affiliate/earnings").then(r => setData(r.data)).catch(() => setFailed(true)); } }, [isAffiliate]);

  if (loading) return <div className="p-16 text-center text-neutral-500 font-mono">LOADING...</div>;
  if (!isAffiliate) return <Navigate to="/affiliazione" replace/>;
  if (failed) return <div className="p-16 text-center label-mono text-alert" data-testid="earnings-error">IMPOSSIBILE CARICARE GLI INCASSI. RIPROVA TRA POCO.</div>;
  if (!data) return <div className="p-16 text-center text-neutral-500 font-mono">LOADING...</div>;

  return (
    <div className="max-w-[1100px] mx-auto px-4 lg:px-8 py-16">
      <div className="label-mono text-neon mb-2">// I_MIEI_INCASSI</div>
      <h1 className="font-display text-4xl lg:text-6xl mb-8">INCASSI</h1>
      <div className="card-dark p-6 mb-8 flex items-center gap-5 border-neon">
        <Wallet className="w-8 h-8 text-neon"/>
        <div>
          <div className="label-mono mb-1">TOTALE CAPARRE RICEVUTE</div>
          <div className="font-display text-4xl text-neon" data-testid="earnings-total">{eur(data.total)}</div>
          <div className="font-mono text-xs text-neutral-500 mt-1" data-testid="earnings-count">{data.count} PAGAMENTI · importi lordi, Stripe trattiene la sua commissione</div>
        </div>
      </div>
      {data.events.length === 0 ? (
        <div className="border border-neutral-800 p-12 text-center" data-testid="earnings-empty">
          <div className="label-mono text-neutral-500 mb-4">// NESSUNA CAPARRA RICEVUTA ANCORA</div>
          <Link to="/tavoli" className="btn-ghost !py-2 !px-4 text-xs">CREA UN TAVOLO</Link>
        </div>
      ) : (
        <div className="space-y-5">{data.events.map(e => <EventBlock key={e.event_id} e={e}/>)}</div>
      )}
    </div>
  );
}
